import axios, { AxiosRequestConfig, AxiosError } from 'axios';
import { BaseQueryFn } from '@reduxjs/toolkit/query';
import { encrypted } from '../../utils/apiUtility';
import { createHeaders } from '../../utils/functions/headers';

export const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: '' }
  ): BaseQueryFn<
    {
      url: string;
      method: AxiosRequestConfig['method'];
      data?: AxiosRequestConfig['data'];
      params?: AxiosRequestConfig['params'];
      headers?: AxiosRequestConfig['headers'];
      encrypt?: boolean;
      decrypt?: boolean;
      responseType?: AxiosRequestConfig['responseType'];
    },
    unknown,
    unknown
  > =>
    async ({
      url,
      method,
      data,
      params,
      headers = createHeaders(),
      encrypt = true,
      decrypt = true,
      responseType,
    }) => {
      try {
        let requestData = data;
        if (encrypt && data) {
          try {
            const encryptedBody = await encrypted.encrypt(JSON.stringify(data));
            requestData = { encryptedBody };
          } catch (e) {
            console.error('Encryption failed:', e);
            return {
              error: {
                status: 500,
                data: 'Encryption failed',
              },
            };
          }
        }

        const result = await axios({
          url: baseUrl + url,
          method,
          data: requestData,
          params,
          headers,
          responseType,
        });

        // Skip decryption if not needed or if responseType is 'blob'
        if (responseType === 'blob' || responseType === 'arraybuffer') {
          return { data: result.data, meta: result };
        }

        const encryptedBody = result.data.encryptedBody || result.data.encryptedBpdy;
        let responseData;

        try {
          if (decrypt && encryptedBody) {
            const decrypted = await encrypted.decrypt(encryptedBody);
            responseData = typeof decrypted === 'string' ? JSON.parse(decrypted) : decrypted;
          } else {
            responseData = result.data;
          }
        } catch (e) {
          console.error('Failed to decrypt response:', e);
          responseData = result.data;
        }

        return { data: responseData, meta: result };
      } catch (axiosError) {
        const err = axiosError as AxiosError;
        return {
          error: {
            status: err.response?.status || 500,
            data:
              (err.response?.data as { message?: string })?.message ||
              'Oops! An unexpected error occurred. Please try again later.',
          },
        };
      }
    };
